import { customElement, property, html, type TemplateResult, css, cssManager } from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-quantityselector.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-quantityselector': DeesInputQuantitySelector;
  }
}

@customElement('dees-input-quantityselector')
export class DeesInputQuantitySelector extends DeesInputBase<DeesInputQuantitySelector> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE

  @property({
    type: Number
  })
  accessor value: number = 1;



  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
        /* TODO: Migrate hardcoded values to --dees-* CSS variables */
        :host {
          width: auto;
          user-select: none;
        }

        .quantity-container {
          transition: all 0.15s ease;
          font-size: var(--dees-font-control-size);
          display: inline-flex;
          align-items: center;
          background: transparent;
          height: var(--dees-control-height-lg);
          padding: 0;
          min-width: 120px;
          color: var(--dees-color-text-primary);
          border: 1px solid var(--dees-color-border-default);
          border-radius: var(--dees-radius-lg);
          overflow: hidden;
        }
        
        .quantity-container.disabled {
          background: var(--dees-color-badge-default-bg);
          border-color: var(--dees-color-border-default);
          opacity: 0.5;
          pointer-events: none;
        }

        .quantity-container:hover:not(.disabled) {
          border-color: var(--dees-color-border-strong);
        }

        .quantity-container:focus-within {
          border-color: var(--dees-color-accent-primary);
          box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
        }

        .selector {
          flex: 0 0 40px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          color: var(--dees-color-text-muted);
          position: relative;
        }

        .selector:hover {
          background: var(--dees-color-badge-default-bg);
          color: var(--dees-color-text-primary);
        }

        .selector:active {
          background: var(--dees-color-border-subtle);
        }

        .selector.minus {
          border-right: 1px solid var(--dees-color-border-default);
        }

        .selector.plus {
          border-left: 1px solid var(--dees-color-border-default);
        }

        .quantity {
          flex: 1;
          text-align: center;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.006em;
        }

        /* Keyboard navigation focus styles */
        .selector:focus {
          outline: none;
          background: var(--dees-color-badge-default-bg);
          z-index: 1;
        }

        /* Min value state */
        .quantity-container[data-min="true"] .selector.minus {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .quantity-container[data-min="true"] .selector.minus:hover {
          background: transparent;
          color: var(--dees-color-text-muted);
        }

    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        ${this.label ? html`<dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>` : ''}
        <div 
          class="quantity-container ${this.disabled ? 'disabled' : ''}"
          data-min="${this.value <= 0}"
        >
          <div 
            class="selector minus" 
            @click="${() => {this.decrease();}}"
            tabindex="${this.disabled ? '-1' : '0'}"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.decrease();
              }
            }}"
            role="button"
            aria-label="Decrease quantity"
          >−</div>
          <div class="quantity" aria-live="polite" aria-atomic="true">${this.value}</div>
          <div 
            class="selector plus" 
            @click="${() => {this.increase();}}"
            tabindex="${this.disabled ? '-1' : '0'}"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.increase();
              }
            }}"
            role="button"
            aria-label="Increase quantity"
          >+</div>
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  public increase() {
    if (!this.disabled) {
      this.value++;
      this.changeSubject.next(this);
    }
  }

  public decrease() {
    if (!this.disabled && this.value > 0) {
      this.value--;
      this.changeSubject.next(this);
    }
  }

  public getValue(): number {
    return this.value;
  }

  public setValue(value: number): void {
    this.value = value;
  }
}
